/**
 * Catalogue signup Phase 1 : activité × espèces × mode.
 * Dual-write vers professions.slug (SEO Booking) ; labels sans « canin ».
 */

export type SignupServiceMode = 'sessions' | 'sitting' | 'stays' | 'grooming' | 'other';

export type SignupActivityKey =
  | 'education'
  | 'behavior'
  | 'pet_sitting'
  | 'walking'
  | 'grooming'
  | 'boarding'
  | 'leisure_sport'
  | 'other';

/** Espèces proposées au signup (ids table `species`). */
export type SignupSpeciesKey = 'dog' | 'cat' | 'horse' | 'nac' | 'other';

export const SIGNUP_SERVICE_MODES: readonly SignupServiceMode[] = [
  'sessions',
  'sitting',
  'stays',
  'grooming',
  'other',
] as const;

/** Activités primaire = cœur SEO Booking (hors nutrition / com / ostéo pour v1). */
export const SIGNUP_ACTIVITIES: readonly {
  key: SignupActivityKey;
  /** Slugs API `professions.slug` à lier (dual-write SEO). */
  professionSlugs: readonly string[];
  /** Modes qui mettent cette activité en avant (auto-select soft). */
  suggestedModes: readonly SignupServiceMode[];
  /** Masquer hors FR si boarding. */
  boardingOnly?: boolean;
}[] = [
  {
    key: 'education',
    professionSlugs: ['trainers'],
    suggestedModes: ['sessions'],
  },
  {
    key: 'behavior',
    professionSlugs: ['behaviorist'],
    suggestedModes: ['sessions'],
  },
  {
    key: 'pet_sitting',
    professionSlugs: ['pet-sitter'],
    suggestedModes: ['sitting'],
  },
  {
    key: 'walking',
    professionSlugs: ['dog-walker'],
    suggestedModes: ['sessions', 'sitting'],
  },
  {
    key: 'grooming',
    professionSlugs: ['groomer'],
    suggestedModes: ['grooming'],
  },
  {
    key: 'boarding',
    professionSlugs: ['pension'],
    suggestedModes: ['stays'],
    boardingOnly: true,
  },
  {
    key: 'leisure_sport',
    professionSlugs: ['loisir-canin', 'sport-canin'],
    suggestedModes: ['sessions'],
  },
  {
    key: 'other',
    professionSlugs: [],
    suggestedModes: ['other'],
  },
] as const;

export const SIGNUP_SPECIES: readonly {
  key: SignupSpeciesKey;
  specieIds: readonly number[];
}[] = [
  { key: 'dog', specieIds: [1] },
  { key: 'cat', specieIds: [2] },
  { key: 'horse', specieIds: [3] },
  /** NAC = oiseau, rongeur, reptile, poisson (hors cheval). */
  { key: 'nac', specieIds: [4, 5, 6, 7] },
  { key: 'other', specieIds: [8] },
] as const;

export type ProfessionSlugRef = { id: number; slug?: string | null };

/** Activités visibles selon pays (pension FR-only). */
export function getSignupActivitiesForCountry(country?: string | null) {
  const allowBoarding =
    !country || country.toUpperCase() === 'FR' || country.toUpperCase() === '';
  return SIGNUP_ACTIVITIES.filter((a) => (a.boardingOnly ? allowBoarding : true));
}

/** Soft-suggest activities when mode(s) change (does not wipe user picks). */
export function suggestedActivityKeysForMode(
  mode: SignupServiceMode | null
): SignupActivityKey[] {
  if (!mode) return [];
  return suggestedActivityKeysForModes([mode]);
}

export function suggestedActivityKeysForModes(
  modes: readonly SignupServiceMode[]
): SignupActivityKey[] {
  if (modes.length === 0) return [];
  const keys = new Set<SignupActivityKey>();
  for (const mode of modes) {
    for (const a of SIGNUP_ACTIVITIES) {
      if (a.suggestedModes.includes(mode)) keys.add(a.key);
    }
  }
  return [...keys];
}

/**
 * Dual-write → profession_ids (activités → slugs catalogue).
 * Espèces ne créent plus de métier (`education-feline` retiré).
 */
export function resolveProfessionIdsFromSignup(params: {
  activityKeys: readonly SignupActivityKey[];
  /** Conservé pour compat appelants ; n’influence plus les métiers. */
  speciesKeys?: readonly SignupSpeciesKey[];
  professions: readonly ProfessionSlugRef[];
}): number[] {
  const { activityKeys, professions } = params;
  const bySlug = new Map<string, number>();
  for (const p of professions) {
    if (p.slug) bySlug.set(p.slug.trim().toLowerCase(), p.id);
  }

  const ids = new Set<number>();
  for (const key of activityKeys) {
    const def = SIGNUP_ACTIVITIES.find((a) => a.key === key);
    if (!def) continue;
    for (const slug of def.professionSlugs) {
      const id = bySlug.get(slug.toLowerCase());
      if (id != null) ids.add(id);
    }
  }

  return [...ids];
}

/** Map signup species keys → specie_id (table `species`). */
export function resolveSpecieIdsFromSignup(
  speciesKeys: readonly SignupSpeciesKey[]
): number[] {
  const ids = new Set<number>();
  for (const key of speciesKeys) {
    const def = SIGNUP_SPECIES.find((s) => s.key === key);
    if (!def) continue;
    for (const id of def.specieIds) ids.add(id);
  }
  return [...ids];
}

/** Inverse specie_ids → clés signup (chien / chat / NAC). */
export function speciesKeysFromSpecieIds(
  specieIds: readonly number[]
): SignupSpeciesKey[] {
  const keys = new Set<SignupSpeciesKey>();
  for (const id of specieIds) {
    for (const def of SIGNUP_SPECIES) {
      if (def.specieIds.includes(id)) keys.add(def.key);
    }
  }
  return [...keys];
}

/** Émojis monochromatisables (carte service, etc.). */
export const SIGNUP_SPECIES_EMOJI: Record<SignupSpeciesKey, string> = {
  dog: '🐕',
  cat: '🐈',
  horse: '🐴',
  nac: '🐹',
  other: '🐾',
};

/** Ordre d’affichage stable des clés signup présentes dans une liste d’ids. */
export function orderedSpeciesKeysFromSpecieIds(
  specieIds: readonly number[] | null | undefined
): SignupSpeciesKey[] {
  if (!specieIds || specieIds.length === 0) return [];
  const present = new Set(speciesKeysFromSpecieIds(specieIds));
  return SIGNUP_SPECIES.map((s) => s.key).filter((k) => present.has(k));
}

/** Slugs SEO du catalogue signup (pour filtrer anciennes grilles métiers). */
export function getSignupCatalogProfessionSlugs(
  country?: string | null
): string[] {
  const slugs = new Set<string>();
  for (const a of getSignupActivitiesForCountry(country)) {
    for (const s of a.professionSlugs) slugs.add(s);
  }
  return [...slugs];
}

const SLUG_TO_ACTIVITY: Record<string, SignupActivityKey> = {
  trainers: 'education',
  behaviorist: 'behavior',
  'pet-sitter': 'pet_sitting',
  'dog-walker': 'walking',
  groomer: 'grooming',
  pension: 'boarding',
  'pension-canine': 'boarding',
  'loisir-canin': 'leisure_sport',
  'sport-canin': 'leisure_sport',
};

/** Inverse profession_ids / slugs → clés activité (hors education-feline). */
export function activityKeysFromProfessionRefs(
  professions: readonly ProfessionSlugRef[],
  professionIds: readonly number[]
): SignupActivityKey[] {
  const byId = new Map(professions.map((p) => [p.id, (p.slug || '').toLowerCase()]));
  const keys = new Set<SignupActivityKey>();
  for (const id of professionIds) {
    const slug = byId.get(id);
    if (!slug) continue;
    const activity = SLUG_TO_ACTIVITY[slug];
    if (activity) keys.add(activity);
  }
  return [...keys];
}

export function isSignupStep1Valid(params: {
  companyName: string;
  /** IDs métiers sélectionnés (catalogue). */
  professionIds: readonly number[];
}): boolean {
  if (!params.companyName.trim()) return false;
  if (!params.professionIds || params.professionIds.length === 0) return false;
  return true;
}
